import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, title, message, onConfirm, confirmText = "Confirm", cancelText = "Cancel", type = "success" }) => {
    if (!isOpen) return null;

    const getIconColor = () => {
        switch (type) {
            case 'success': return 'text-green-600 bg-green-100';
            case 'warning': return 'text-amber-600 bg-amber-100';
            case 'error': return 'text-red-600 bg-red-100';
            default: return 'text-blue-600 bg-blue-100';
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                );
            case 'warning':
                return (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                );
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
                <div className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getIconColor()}`}>
                            {getIcon()}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                            <p className="text-sm text-gray-600 mt-1">{message}</p>
                        </div>
                    </div>
                    <div className="flex space-x-3 justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            {cancelText}
                        </button>
                        <button
                            onClick={() => {
                                if (onConfirm) onConfirm();
                                onClose();
                            }}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Step3Deploy = ({
    createdWidget,
    widgetConfig,
    onCopyToClipboard,
    onResetWidget
}) => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({});

    const embedCode = createdWidget?.embedCode || `<div id="rannkly-widget-${createdWidget?._id || 'demo'}"></div>
<script src="http://localhost:8000/widgets/embed/${createdWidget?._id || 'demo'}.js"></script>`;

    const handleCopyToClipboard = () => {
        setModalConfig({
            title: "Copy to Clipboard",
            message: "Are you sure you want to copy the widget code to your clipboard?",
            type: "success",
            confirmText: "Copy Code",
            onConfirm: async () => {
                await onCopyToClipboard(embedCode);
                // Show success modal after copying
                setTimeout(() => {
                    setModalConfig({
                        title: "Success!",
                        message: "Widget code has been copied to your clipboard successfully!",
                        type: "success",
                        confirmText: "Got it",
                        onConfirm: null
                    });
                    setShowModal(true);
                }, 100);
            }
        });
        setShowModal(true);
    };

    const handleResetWidget = () => {
        setModalConfig({
            title: "Create New Widget",
            message: "This will reset your current progress and start creating a new widget. Are you sure?",
            type: "warning",
            confirmText: "Reset & Create New",
            onConfirm: onResetWidget
        });
        setShowModal(true);
    };

    return (
        <div className="max-w-6xl mx-auto">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
                <span className="text-gray-400">Template Selection</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-gray-400">Configuration</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-blue-600 font-medium">Deployment</span>
            </div>

            {/* Success Header - More Compact */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Widget Created Successfully! 🎉</h2>
                <p className="text-gray-600 max-w-xl mx-auto">Your review widget is ready to enhance your website&apos;s credibility</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Widget Code - Compact */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Embed Code</h3>
                        </div>
                        <button
                            onClick={handleCopyToClipboard}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy</span>
                        </button>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-64 overflow-y-auto">
                        <code className="text-xs text-gray-800 whitespace-pre-wrap break-all font-mono leading-relaxed">
                            {embedCode}
                        </code>
                    </div>
                </div>

                {/* Quick Setup Guide - Compact */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Setup Guide</h3>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">1</div>
                            <p className="text-sm text-gray-700">Copy the embed code</p>
                        </div>
                        <div className="flex items-start space-x-3">
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">2</div>
                            <p className="text-sm text-gray-700">Paste in your website&apos;s HTML</p>
                        </div>
                        <div className="flex items-start space-x-3">
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">3</div>
                            <p className="text-sm text-gray-700">Widget loads automatically!</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Implementation Example - More Compact */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    <span>HTML Example</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-48 overflow-y-auto">
                    <code className="text-sm text-green-400 whitespace-pre font-mono">
                        {`<!DOCTYPE html>
<html>
<head>
    <title>Your Website</title>
</head>
<body>
    <h1>Welcome to Our Store</h1>
    
    <!-- Your Rannkly Widget -->
    ${embedCode.substring(0, 200)}...
    
    <p>Other content...</p>
</body>
</html>`}
                    </code>
                </div>
            </div>

            {/* Widget Details - New Compact Section */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Widget Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                        <div className="text-sm text-gray-600 mb-1">Widget Type</div>
                        <div className="font-semibold text-gray-900">{widgetConfig?.selectedTemplate?.name || 'Custom Widget'}</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                        <div className="text-sm text-gray-600 mb-1">Widget ID</div>
                        <div className="font-mono text-sm text-gray-900">{createdWidget?._id?.substring(0, 8) || 'demo'}...</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                        <div className="text-sm text-gray-600 mb-1">Status</div>
                        <div className="font-semibold text-green-600 flex items-center space-x-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span>Active</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions - More Compact */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center space-x-2 bg-white text-gray-700 px-6 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-medium border border-gray-300 hover:border-gray-400"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Back to Dashboard</span>
                </button>
                <button
                    onClick={() => navigate('/widgets')}
                    className="flex items-center space-x-2 bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span>Manage Widgets</span>
                </button>
                <button
                    onClick={handleResetWidget}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Create Another Widget</span>
                </button>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                confirmText={modalConfig.confirmText}
                onConfirm={modalConfig.onConfirm}
            />
        </div>
    );
};

export default Step3Deploy; 